<template>
  <div id="inspire">
    <div class="backgroundWindow flex justify-center items-center p-0 h-screen px-4">
      <div class="bg-white card rounded-xl card_design w-full px-6 sm:px-12 py-8 sm:m-0 shadow-2xl">
        <div class="text-center flex flex-col items-center">
          <!-- Logo -->
          <img src="@/assets/images/ZizbeyJobsLogo.svg" class="h-12 mb-2" />
          
          <!-- Title -->
          <div class="heading-2 text-primary font-semibold pl-1">ZizbeyJobs</div>
          
          <!-- Subtitle -->
          <div class="font-semibold heading-3 text-center text-text1 py-2">
            Welcome back
          </div>

          <!-- Country Code and Mobile Number -->
          <div class="mb-6 w-full flex justify-center items-center gap-4 sm:gap-6 mt-4">
            
            <!-- Country Code Dropdown -->
            <div class="w-32 relative">
              <p class="flex text-sm text-gray-500 mb-1 font-medium">Code</p>
              <div 
                type="text" 
                @click="clickOnCode()"
                class="flex items-center justify-between cursor-pointer outline-none border border-gray2 text-text1 rounded cust_text_field w-full pl-3 pr-10 h-12 focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-white"
              >
                <span class="truncate">
                  {{ selectedCountry.emoji || '🇮🇳' }} {{ payload.countryCode }}
                </span>
                <i class="fas fa-caret-down text-gray-400"></i>
              </div>
            </div>

            <!-- Mobile Number Input -->
            <div class="w-full sm:w-2/3">
              <TextField
                :inputId="'mobileid'"
                :inputext="payload.mobile"
                :placholderText="'Enter mobile number'"
                :lableText="'Mobile Number'"
                :fieldError="mobileErr !== ''"
                :autoFocus="true"
                :inputType="'tel'"
                @keyPressAction="allowOnlyDigits"
                @enterkeyPressAction="sendOtp"
                @blurAction="onMobileBlur"
                @inputChangeAction="(data) => handleMobileInput(data)"
              />
              <p v-if="mobileErr" class="text-error text-xs text-left mt-1">{{ mobileErr }}</p>
            </div>
          </div>

          <!-- Send OTP Button with Validation Logic -->
          <div class="py-2">
            <Button
              class="flex text-center justify-center"
              :class="[
                isValidMobile ? 'opacity-100 cursor-pointer' : 'opacity-50 cursor-not-allowed grayscale'
              ]"
              :btnSize="'medium'"
              :textColor="'white'"
              :btnColor="'primary'"
              :btnText="'Send OTP'"
              :disabled="!isValidMobile" 
              @buttonAction="sendOtp"
            />
            
            <!-- Helper Text (Optional: Shows why button is disabled) -->
            <p v-if="!isValidMobile && payload.mobile.length > 0" class="text-xs text-gray-400 mt-2">
              Please enter a valid 10-digit number
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loader & Snackbar -->
    <loader
      v-if="$store.getters.showLoader"
      v-bind:data="$store.getters.loaderMessage"
    ></loader>
    <snakBar
      v-if="$store.getters.showAlert"
      v-bind:status="$store.getters.showAlert"
      v-bind:message="$store.getters.alertMessage"
      v-bind:color="$store.getters.alertColor"
    ></snakBar>

    <!-- Country Selection Popup -->
    <div v-if="openTimePopup" class="popup_overlay px-4 fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="custom_dialog rounded-xl bg-white w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl animate-fade-in-up">
        
        <!-- Header -->
        <div class="bg-primary flex p-4 text-center justify-center sticky top-0 rounded-t-xl">
          <div class="font-medium text-white heading-3">Select Country</div>
        </div>

        <!-- Search -->
        <div class="p-3 border-b border-gray-100">
          <SearchTextInput @searchCall="searchCall" :placeholderText="`Search countries...`"/>
        </div>

        <!-- List -->
        <div style="max-height: 50vh" class="overflow-auto p-2 px-2">
          <div v-for="(data, index) in filteredCountries" :key="data.countryId || index" :id="data.phoneCode">
            <div 
              class="flex items-center py-3 px-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
              :class="{ 'bg-blue-50 border border-blue-200': radioModel === data.countryId }"
              @click="clickOnRadio(data)"
            >
              <input 
                type="radio" 
                class="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 cursor-pointer focus:ring-blue-500" 
                v-model="radioModel" 
                name="radio1" 
                :value="data.countryId"
              >
              <div class="pl-3 flex items-center">
                <span class="text-2xl mr-3">{{ data.emoji || '🌍' }}</span>
                <div class="flex text-text2 cursor-pointer">
                  <p class="font-medium text-gray-800">{{ data.countryName }}</p>
                  <p class="text-gray-500 ml-1">({{ data.phoneCode }})</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Empty State -->
          <div v-if="filteredCountries.length === 0" class="text-center py-8 text-gray-500">
            No countries found
          </div>
        </div>

        <!-- Footer -->
        <div class="sticky bottom-0 bg-white w-full flex justify-end p-4 border-t border-gray-100 rounded-b-xl">
          <Button 
            :btnSize="'medium'" 
            :textColor="'text1'" 
            :btnColor="'gray1'" 
            class="border-gray-300"
            :btnText="'Cancel'" 
            @buttonAction="openTimePopup = false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/View/components/Loader.vue'
import snakBar from '@/View/components/SnakBar.vue'
import TextField from '@/View/components/textfield.vue'
import Button from '@/View/components/globalButton.vue'
import MyJobApp from '@/api/MyJobApp.js'
import SearchTextInput from '@/View/components/searchTextInput.vue'
import axios from 'axios'
import moment from "moment-timezone"

export default {
  components: { Loader, snakBar, TextField, Button, SearchTextInput },
  data() {
    return {
      mobile: '', 
      mobileErr: '',
      regToken: '',
      selectedCountryCode: '+91',
      openTimePopup: false,
      countries: [],
      backupCountries: [],
      searchQuery: '',
      radioModel: null, 
      payload: {
        mobile: '',
        countryCode: '91',
      },
      secondObj: {
        countryCode: '',
        deviceCountryCode: '',
        callingCode: '+91',
        cellNumber: '',
        industryTypeId: 0,
        selectedCompanyName: '',
        companySize:'',
        estimatedAnnualRevenue: '',
        source: '',
        timeZone: ''
      },
      selectedCountry: {}
    };
  },
  mounted () {
    this.$store.dispatch('SetLoader', { status: true, message: 'Loading...' })
    this.getCoutryCode()
    this.getLoginIpData()
  },
  computed: {
    filteredCountries() {
      const q = this.searchQuery.toLowerCase().trim()
      if (!q) return this.countries
      return this.countries.filter(c =>
        Object.values(c).some(v => String(v).toLowerCase().includes(q))
      )
    },
    // ✅ NEW: Real-time validation check
    // Returns TRUE only if length is 10 AND contains only digits
    isValidMobile() {
      const mobile = this.payload.mobile;
      // Allow special test number
      if (mobile === '3322332233') return true;

      const regex = /^[6-9]\d{9}$/; // Must start with 6, 7, 8, or 9 followed by 9 digits
      return regex.test(mobile);
    }
  },
  methods: {
    // ✅ Prevents non-digit keystrokes physically
    allowOnlyDigits(e) {
      const char = String.fromCharCode(e.keyCode || e.which)
      if (!/[0-9]/.test(char)) {
        e.preventDefault()
        return false
      }
    },
    
    // ✅ Cleans input on paste/type
    handleMobileInput(val) {
      // Remove everything except digits, limit to 10 chars
      const cleanVal = val.replace(/\D/g, '').slice(0, 10)
      this.payload.mobile = cleanVal
      
      // Clear error if valid
      if (cleanVal.length === 10) {
        this.mobileErr = ''
      }
    },

    // ✅ Strict Validation Function (Double Check)
    validateMobile(number) {
      // ✅ Allow special number without validation
      if (number === '3322332233') {
        this.mobileErr = '';
        return true;
      }
      if (!number || number.length === 0) {
        this.mobileErr = 'Mobile number is required';
        return false;
      }
      if (number.length !== 10) {
        this.mobileErr = 'Mobile number must be 10 digits';
        return false;
      }
      if (!/^[6-9]/.test(number))  {
        this.mobileErr = 'Mobile numbers must be a valid number';
        return false;
      }
      if (!/^\d+$/.test(number)) {
        this.mobileErr = 'Invalid characters in mobile number';
        return false;
      }
      this.mobileErr = '';
      return true;
    },
    
    onMobileBlur() {
      this.validateMobile(this.payload.mobile);
    },

    clickOnCode () {
      if (this.selectedCountry && this.selectedCountry.countryId) {
        this.radioModel = this.selectedCountry.countryId
      } else {
        this.radioModel = this.secondObj.callingCode
      }
      this.openTimePopup = true
      this.$nextTick(() => {
        setTimeout(() => {
          const targetId = this.selectedCountry.countryId ? 
            document.querySelector(`input[value="${this.selectedCountry.countryId}"]`) : 
            document.getElementById(this.secondObj.callingCode)
          if (targetId && targetId.scrollIntoView) {
            targetId.scrollIntoView({ behavior: "smooth", block: 'center' })
          }
        }, 100)
      })
    },

    clickOnRadio (data) {
      this.selectedCountry = data
      const cleanCode = String(data.phoneCode).replace('+', '').trim()
      this.payload.countryCode = cleanCode
      this.secondObj.callingCode = '+' + cleanCode
      this.secondObj.countryCode = data.iso2
      this.radioModel = data.countryId
      this.openTimePopup = false
      this.searchQuery = ''
    },

    searchCall (data) {
      this.searchQuery = data
    },

    getCoutryCode () {
      MyJobApp.GetPhoneCountryCode(
        (response) => {
          let temp = response.Data !== null ? response.Data : []
          this.countries = temp.map(c => ({
            ...c,
            phoneCode: String(c.phoneCode).replace('+', ''),
            emoji: c.emoji || this.getEmojiFromISO(c.iso2)
          }))
          this.backupCountries = [...this.countries]
          this.parentCityList = this.countries
          this.timeZoneList = this.countries

          this.$nextTick(() => {
            this.detectCountryByTimezone()
            this.sortCountries()
          })
          this.$store.dispatch('SetLoader', { status: false, message: '' })
        },
        error => {
          this.$store.dispatch('SetLoader', { status: false, message: '' })
          this.$store.dispatch('SetAlert', { showAlert: true, message: error.message, color: 'error' })
        }
      ) 
    },

    getEmojiFromISO(iso2) {
      if (!iso2 || iso2.length !== 2) return '🇮🇳'
      return iso2.toUpperCase().split('').map(char => String.fromCodePoint(127397 + char.charCodeAt(0))).join('')
    },

    detectCountryByTimezone() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (!tz) return
        const zone = moment.tz.zone(tz)
        if (!zone || !zone.countries || zone.countries.length === 0) return
        const country = this.backupCountries.find(c => c.iso2 && zone.countries.includes(c.iso2))
        if (country) {
          this.clickOnRadio(country)
        } else if (this.countries.length > 0) {
          this.clickOnRadio(this.countries[0])
        }
      } catch (err) {
        console.warn("Timezone detection failed", err)
      }
    },

    sortCountries() {
      const famousOrder = ['IN', 'US', 'AE', 'GB', 'CA']
      const selISO = this.selectedCountry?.iso2
      this.countries = [...this.backupCountries].sort((a, b) => {
        if (a.iso2 === selISO) return -1
        if (b.iso2 === selISO) return 1
        const aIndex = famousOrder.indexOf(a.iso2)
        const bIndex = famousOrder.indexOf(b.iso2)
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1
        return (a.countryName || '').localeCompare(b.countryName || '')
      })
    },

    sendOtp() {
      // 🛑 SAFETY CHECK 1: Computed Property
      // If this is false, the button should be disabled, but we check again here.
      if (!this.isValidMobile) {
        this.mobileErr = 'Please enter a valid 10-digit mobile number';
        return; // STOP: API Call Prevented
      }

      // 🛑 SAFETY CHECK 2: Full Validation
      if (!this.validateMobile(this.payload.mobile)) {
        return; // STOP: API Call Prevented
      }

      const fullMobileNumber = this.secondObj.callingCode + this.payload.mobile;
       
      this.$store.dispatch('SetLoader', { status: true, message: 'Sending OTP...' })

      MyJobApp.SendOtp(
        fullMobileNumber,
        (response) => {
          this.$store.dispatch('SetLoader', { status: false, message: '' });
          if (response && response.success === 1 && response.Data) {
            this.regToken = response.Data;
            this.$store.dispatch('SetAlert', { showAlert: true, message: 'OTP sent successfully', color: 'success' });
            this.$router.push({ name: 'verifyOtp', query: { mobile: fullMobileNumber, token: this.regToken } });
          } else {
            this.$store.dispatch('SetAlert', { showAlert: true, message: response?.message || 'Something went wrong', color: 'error' });
          }
        },
        (error) => {
          this.$store.dispatch('SetLoader', { status: false, message: '' });
          this.$store.dispatch('SetAlert', { showAlert: true, message: error?.message || 'Failed to send OTP', color: 'error' });
        }
      );
    },

    getLoginIpData () {
      let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      this.secondObj.timeZone = (timeZone === 'Asia/Calcutta') ? 'Asia/Kolkata' : timeZone
      
      axios.get(`https://ipapi.co/json/`)
      .then(data => {
        if (data.status === 200) {
          this.secondObj.deviceCountryCode = ['in', 'us', 'gb'].includes(data.data.country.toLowerCase()) ? data.data.country : 'OTH'
          this.secondObj.countryCode = data.data.country_code
          if (!this.selectedCountry.countryId) {
             const code = data.data.country_calling_code
             this.secondObj.callingCode = code
             this.payload.countryCode = code.replace('+', '')
          }
        }
      }).catch(Error => console.log('IP API Error', Error))
    }
  }
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css?family=Saira+Stencil+One&display=swap");

.backgroundWindow {
  background: url('https://workapp-public-download.s3.ap-south-1.amazonaws.com/assets/signup-in-background.jpg');
  background-size: cover;
  background-position: center;
}

.card_design {
  max-width: 500px;
}

/* Animation for Popup */
@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in-up {
  animation: fade-in-up 0.3s ease-out forwards;
}

/* Custom Scrollbar for Popup */
.overflow-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.overflow-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
</style>